import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTaskSuggestions, createTask } from '../utils/api';
import { useApp } from '../context/AppContext';

const FREQ_LABELS = {
  weekly: 'Weekly', monthly: 'Monthly', quarterly: 'Quarterly',
  biannual: 'Biannual', annual: 'Annual', custom: 'Custom',
};

const TaskSuggestions = () => {
  const navigate = useNavigate();
  const { fetchTasks } = useApp();
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleGenerate = async () => {
    setLoading(true);
    setError('');
    setSuggestions([]);
    setSelected(new Set());
    try {
      const { data } = await getTaskSuggestions();
      setSuggestions(data.suggestions || []);
      setSelected(new Set(data.suggestions.map((_, i) => i)));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to get suggestions. Make sure your home profile is filled out.');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (i) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const handleAddSelected = async () => {
    const toAdd = suggestions.filter((_, i) => selected.has(i));
    if (!toAdd.length) return;
    setSaving(true);
    try {
      await Promise.all(toAdd.map((s) => createTask(s)));
      await fetchTasks({ force: true });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create tasks');
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div className="page">
        <div className="empty-state-card">
          <p style={{ fontSize: '2rem' }}>✅</p>
          <p>{selected.size} task{selected.size !== 1 ? 's' : ''} added to your schedule.</p>
          <button className="btn btn-primary" style={{ marginTop: '1rem' }} onClick={() => navigate('/tasks')}>
            View Tasks
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>AI Task Suggestions</h1>
          <p className="page-subtitle">Claude analyzes your home profile and suggests maintenance tasks to schedule.</p>
        </div>
        <button className="btn btn-secondary" onClick={() => navigate('/tasks')}>Back to Tasks</button>
      </div>

      {suggestions.length === 0 && (
        <div className="intake-start-card">
          <p>Claude will look at your home's age, systems, appliances, and existing tasks to suggest what's missing from your maintenance schedule.</p>
          <button
            className="btn btn-primary"
            onClick={handleGenerate}
            disabled={loading}
            style={{ marginTop: '1.5rem' }}
          >
            {loading ? 'Analyzing your home…' : '✨ Generate Suggestions'}
          </button>
          {loading && <p className="page-subtitle" style={{ marginTop: '1rem' }}>This takes about 15–20 seconds…</p>}
          {error && <p className="form-error" style={{ marginTop: '1rem' }}>{error}</p>}
        </div>
      )}

      {suggestions.length > 0 && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <p className="page-subtitle">
              {selected.size} of {suggestions.length} selected — review and uncheck any you don't want.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setSelected(new Set(suggestions.map((_, i) => i)))}>
                Select All
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => setSelected(new Set())}>
                Deselect All
              </button>
            </div>
          </div>

          <div className="suggestions-list">
            {suggestions.map((s, i) => (
              <div
                key={i}
                className={`suggestion-card${selected.has(i) ? ' selected' : ''}`}
                onClick={() => toggleSelect(i)}
              >
                <div className="suggestion-check">
                  <input
                    type="checkbox"
                    checked={selected.has(i)}
                    onChange={() => toggleSelect(i)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div className="suggestion-body">
                  <div className="suggestion-title">{s.title}</div>
                  <div className="suggestion-meta">
                    <span className="badge badge-category">{s.category}</span>
                    <span className="badge">{FREQ_LABELS[s.frequency] || s.frequency}</span>
                    {s.estimatedMinutes && <span className="badge">{s.estimatedMinutes}m</span>}
                    {s.professionalCost > 0 && <span className="badge badge-success">Pro: ${s.professionalCost}</span>}
                    {s.nextDue && <span className="badge">Due {new Date(s.nextDue).toLocaleDateString()}</span>}
                  </div>
                  {s.notes && <p className="suggestion-notes">{s.notes}</p>}
                </div>
              </div>
            ))}
          </div>

          {error && <p className="form-error">{error}</p>}

          <div className="form-actions form-actions-bottom" style={{ marginTop: '1.5rem' }}>
            <button
              className="btn btn-primary"
              onClick={handleAddSelected}
              disabled={saving || selected.size === 0}
            >
              {saving ? 'Adding…' : `Add ${selected.size} Task${selected.size !== 1 ? 's' : ''}`}
            </button>
            <button className="btn btn-secondary" onClick={handleGenerate} disabled={loading}>
              {loading ? 'Generating…' : 'Regenerate'}
            </button>
            <button className="btn btn-secondary" onClick={() => navigate('/tasks')}>Cancel</button>
          </div>
        </>
      )}
    </div>
  );
};

export default TaskSuggestions;
